import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'
import SuburbInput from '../components/SuburbInput'
import { track } from '../lib/analytics'
import { describeSignUpError, describeSignInError, looksLikeEmail } from '../lib/authErrors'

// People arriving to book, message or save are almost always new — show sign-up first
const SIGNUP_FIRST_REASONS = ['book', 'message', 'favourite']

const fieldError = (msg) => msg ? <div className="form-error" style={{ marginTop: 6 }}>{msg}</div> : null
const errorBorder = (msg) => msg ? { borderColor: 'var(--red)' } : undefined

export default function Auth() {
const [searchParams] = useSearchParams()
const refCode = searchParams.get('ref')
const nextParam = searchParams.get('next')
const safeNext = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : null
const reason = searchParams.get('reason')
const source = reason || (refCode ? 'referral' : searchParams.get('from')) || 'direct'
const [mode, setMode] = useState(searchParams.get('mode') === 'signup' || SIGNUP_FIRST_REASONS.includes(reason) ? 'signup' : 'login')
const [email, setEmail] = useState('')
const [password, setPassword] = useState('')
const [showPassword, setShowPassword] = useState(false)
const [fullName, setFullName] = useState('')
const [suburb, setSuburb] = useState('')
const [termsAccepted, setTermsAccepted] = useState(false)
const [loading, setLoading] = useState(false)
const [errors, setErrors] = useState({})
const [notice, setNotice] = useState('')
const { signIn, signUp, user } = useAuth()
const navigate = useNavigate()
// True while sign-up is in flight: Supabase signs the new user in before
// signUp() resolves, and we don't want the "already logged in" redirect
// below to beat the redirect to onboarding.
const signingUpRef = useRef(false)
const viewTrackedRef = useRef(false)

useEffect(() => {
if (mode === 'signup' && !viewTrackedRef.current) {
viewTrackedRef.current = true
track('sign_up_view', { source })
}
}, [mode, source])

useEffect(() => {
if (user && !signingUpRef.current) navigate(safeNext || '/')
}, [user])

const switchMode = () => {
const to = mode === 'login' ? 'signup' : 'login'
track('sign_up_switch', { to, source })
setMode(to)
setErrors({}); setNotice(''); setTermsAccepted(false)
}

const goToOnboarding = () => navigate(`/onboarding${safeNext ? `?next=${encodeURIComponent(safeNext)}` : ''}`)

const handleLogin = async () => {
const errs = {}
if (!looksLikeEmail(email)) errs.email = 'Please enter your email address.'
if (!password) errs.password = 'Please enter your password.'
setErrors(errs)
if (Object.keys(errs).length) return
setLoading(true)
const { error } = await signIn(email.trim(), password)
setLoading(false)
if (error) setErrors({ general: describeSignInError(error) })
else navigate(safeNext || '/')
}

const handleSignUp = async () => {
track('sign_up_submit', { source })
const errs = {}
if (!fullName.trim()) errs.name = 'Please enter your full name.'
if (!suburb.trim()) errs.suburb = 'Please enter your suburb.'
if (!email.trim()) errs.email = 'Please enter your email address.'
else if (!looksLikeEmail(email)) errs.email = "That email address doesn't look right — please check it."
if (password.length < 6) errs.password = 'Password must be at least 6 characters.'
if (!termsAccepted) errs.terms = 'Please tick the box to accept the Terms and Conditions.'
setErrors(errs)
if (Object.keys(errs).length) {
track('sign_up_error', { reason: Object.keys(errs).map(k => `missing_${k}`).join(',').slice(0, 100), source })
// Bring the first error into view (it can be off-screen on a phone)
requestAnimationFrame(() => document.querySelector('.form-error, .alert-error')?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
return
}

setLoading(true)
signingUpRef.current = true
const { data, error } = await signUp(email.trim(), password, fullName.trim(), suburb.trim(), refCode)
// Supabase hides "already registered" behind an empty identities list when it can't say so directly
const alreadyRegistered = !error && data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0
if (error || alreadyRegistered) {
signingUpRef.current = false
setLoading(false)
const info = alreadyRegistered ? describeSignUpError({ code: 'user_already_exists' }) : describeSignUpError(error)
setErrors(info.field ? { [info.field]: info.message, alreadyRegistered: info.reason === 'already_registered' } : { general: info.message })
track('sign_up_error', { reason: info.reason, source })
// Bring the first error into view (it can be off-screen on a phone)
requestAnimationFrame(() => document.querySelector('.form-error, .alert-error')?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
return
}

track('sign_up', { method: 'email', form: 'auth', source })
if (data?.session) {
// Emails are auto-confirmed on this project, so the user is already signed in
goToOnboarding()
return
}
// Only reachable if Supabase is ever switched to require email confirmation
signingUpRef.current = false
setLoading(false)
setNotice(`Your account has been created. We've sent a link to ${email.trim()} — open it to finish signing in.`)
}

const handleSubmit = () => (mode === 'login' ? handleLogin() : handleSignUp())
const onEnter = e => { if (e.key === 'Enter') handleSubmit() }

return (
<div>
<Header />
<div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 1rem' }}>
<div style={{ width: '100%', maxWidth: 420 }}>
<div style={{ textAlign: 'center', marginBottom: '2rem' }}>
<div style={{ fontSize: 40, marginBottom: 8 }}>🔑</div>
<h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--navy)' }}>{mode === 'login' ? 'Welcome back' : 'Join HireIt'}</h1>
<p style={{ color: 'var(--text-muted)', marginTop: 4 }}>
{mode === 'login' ? 'Sign in to your account' : 'Free to join'}
</p>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>
{mode === 'login' ? 'New to HireIt? ' : 'Already have an account? '}
<button onClick={switchMode} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: 0 }}>
{mode === 'login' ? 'Create a free account' : 'Sign in'}
</button>
</p>
</div>

{safeNext && !reason && mode === 'login' && (
<div className="alert alert-info" style={{ marginBottom: '1rem', textAlign: 'left' }}>
Please sign in to continue — we'll take you straight back to where you were.
</div>
)}

{(reason === 'book' || reason === 'message' || reason === 'favourite') && (
<div className="alert alert-info" style={{ marginBottom: '1rem', textAlign: 'left' }}>
<strong>You're almost there!</strong> {reason === 'book'
? 'Create a free account (or sign in) to complete your booking — your booking details are saved.'
: reason === 'message'
? 'Create a free account (or sign in) to message the owner — your draft is saved.'
: 'Create a free account (or sign in) to save your favourites.'}
</div>
)}

<div className="card card-body">
{mode === 'signup' && (
<div className="form-group">
<label className="form-label" htmlFor="signup-name">Full name</label>
<input id="signup-name" className="form-control" placeholder="Your full name" value={fullName} onChange={e => setFullName(e.target.value)} autoComplete="name" autoCapitalize="words" onKeyDown={onEnter} style={errorBorder(errors.name)} />
{fieldError(errors.name)}
</div>
)}
{mode === 'signup' && (
<div className="form-group">
<label className="form-label" htmlFor="signup-suburb">Suburb</label>
<SuburbInput id="signup-suburb" value={suburb} onChange={setSuburb} hasError={!!errors.suburb} />
{fieldError(errors.suburb)}
</div>
)}
<div className="form-group">
<label className="form-label" htmlFor="auth-email">Email address</label>
<input id="auth-email" className="form-control" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={onEnter} style={errorBorder(errors.email)} />
{errors.email && (
<div className="form-error" style={{ marginTop: 6 }}>
{errors.email}
{errors.alreadyRegistered && (
<> <button type="button" onClick={switchMode} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>Sign in</button></>
)}
</div>
)}
</div>
<div className="form-group">
<label className="form-label" htmlFor="auth-password">Password</label>
<div style={{ position: 'relative' }}>
<input id="auth-password" className="form-control" type={showPassword ? 'text' : 'password'} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} placeholder={mode === 'login' ? '••••••••' : 'At least 6 characters'} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={onEnter} style={{ paddingRight: 44, ...errorBorder(errors.password) }} />
<button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
{showPassword ? (
<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
) : (
<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
)}
</button>
</div>
{fieldError(errors.password)}
</div>

{mode === 'signup' && (
<div style={{ border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
<div style={{ maxHeight: 170, overflowY: 'auto', paddingRight: 8, marginBottom: 10 }}>
<p style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', margin: '0 0 8px', letterSpacing: 0.3 }}>IMPORTANT</p>
<p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 6px' }}>
By completing and submitting this form You will create a binding agreement between You and Us incorporating the <a href="/terms" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Standard Terms and Conditions</a>.
</p>
<p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 4px' }}>By completing and submitting this form You warrant that:</p>
<ul style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 6px', paddingLeft: 18 }}>
<li>You have read the Terms and Conditions;</li>
<li>You have not submitted more than one Application (unless We have given You permission otherwise);</li>
<li>You are over 18 years of age; and</li>
<li>all the information given in this Application is correct.</li>
</ul>
<p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 4px' }}>Your use of the Platform will be governed by the Terms and Conditions and You acknowledge that:</p>
<ul style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 6px', paddingLeft: 18 }}>
<li>You must not use, and must cease to use, the Platform if You do not agree or cease to agree to the Terms and Conditions; and</li>
<li>You have the opportunity to obtain independent legal advice about the Terms and Conditions.</li>
</ul>
<p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 0' }}>
Words and phrases used in this form have the meanings set out in the Terms and Conditions and will be interpreted in accordance with the rules of interpretation set out therein.
</p>
</div>
<label style={{ display: 'flex', gap: 10, cursor: 'pointer', fontSize: 13, lineHeight: 1.6, borderTop: '1px solid var(--border-strong)', paddingTop: 10 }}>
<input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
<span>I have read and agree to the <a href="/terms" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>Terms and Conditions</a>.</span>
</label>
</div>
)}
{mode === 'signup' && errors.terms && <div className="form-error" style={{ marginTop: -4, marginBottom: 12 }}>{errors.terms}</div>}

{errors.general && <div className="alert alert-error">{errors.general}</div>}
{notice && <div className="alert alert-success">{notice}</div>}
<button className="btn btn-gradient btn-lg btn-full" onClick={handleSubmit} disabled={loading}>
{loading ? (mode === 'login' ? 'Signing in...' : 'Creating account...') : mode === 'login' ? 'Sign in' : 'Create account'}
</button>
{mode === 'login' && (
<p style={{ textAlign: 'center', fontSize: 13, marginTop: 12, marginBottom: 0 }}>
<Link to={`/forgot-password?${new URLSearchParams({ ...(email.trim() ? { email: email.trim() } : {}), ...(safeNext ? { next: safeNext } : {}) })}`} style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}>Forgot password?</Link>
</p>
)}
<p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 16 }}>
{mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
<button onClick={switchMode}
style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
{mode === 'login' ? 'Sign up' : 'Sign in'}
</button>
</p>
</div>
</div>
</div>
</div>
)
}
